import { motion } from "framer-motion";
import { Check, Minus } from "lucide-react";

const columns = ["Toppr", "Google Classroom", "Tata ClassEdge", "ProBodh"];
const rows = [
  ["Teacher creates own content", "no", "yes", "yes", "yes"],
  ["Voice narration in Indian languages", "yes", "no", "no", "yes"],
  ["Homework with AI grading", "no", "partial", "no", "yes"],
  ["Gradebook & marks tracking", "no", "yes", "yes", "yes"],
  ["Parent updates with AI summary", "partial", "partial", "partial", "yes"],
  ["Custom answer rubrics per school", "no", "no", "no", "yes-unique"],
  ["Past paper question bank", "no", "no", "no", "yes"],
  ["AI test paper generator", "no", "no", "no", "yes"],
  ["Works for private tuitions", "no", "yes", "no", "yes"],
  ["Offline / low bandwidth mode", "no", "no", "no", "yes"],
  ["Built-in self-study (Feynman flow)", "no", "no", "no", "yes-unique"],
  ["Three depth levels per topic", "no", "no", "no", "yes-unique"],
  ["AI Thinking Coach — never gives answers", "no", "no", "no", "yes-unique"],
];

function Cell({ value, isPB }) {
  const base =
    "px-3 md:px-4 py-3.5 md:py-4 text-center align-middle border-b border-line-soft";
  if (value === "yes-unique") {
    return (
      <td className={`${base} ${isPB ? "bg-mint/50" : ""}`}>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ink text-white-t text-[11px] font-semibold">
          <Check size={12} strokeWidth={2.5} /> Unique
        </span>
      </td>
    );
  }
  if (value === "yes") {
    return (
      <td className={`${base} ${isPB ? "bg-mint/40" : ""}`}>
        <Check
          size={18}
          strokeWidth={2.5}
          className={`inline ${isPB ? "text-ink" : "text-ink"}`}
        />
      </td>
    );
  }
  if (value === "partial") {
    return (
      <td className={base}>
        <span className="inline-flex items-center gap-1 text-[11px] md:text-[12px] font-semibold text-ink-muted uppercase tracking-wide">
          Partial
        </span>
      </td>
    );
  }
  return (
    <td className={base}>
      <Minus size={16} className="inline text-ink-muted/60" />
    </td>
  );
}

export default function Comparison() {
  return (
    <section
      id="compare"
      className="bg-off py-16 md:py-24"
      data-testid="comparison-section"
    >
      <div className="mx-auto max-w-[1200px] px-5 md:px-8">
        <div className="max-w-3xl mb-8 md:mb-12">
          <div className="text-[12px] md:text-[13px] uppercase tracking-[0.18em] text-ink-muted font-semibold">
            An honest chart · Compare
          </div>
          <h2 className="mt-3 md:mt-4 font-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-ink tracking-tight">
            What ProBodh does that{" "}
            <span className="font-italic-serif text-accent">nobody else</span>{" "}
            does.
          </h2>
          <p className="mt-4 text-[14px] md:text-[16px] leading-relaxed text-ink-soft max-w-[52ch]">
            Based on publicly available product documentation, 2025. Every row is
            verifiable.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="rounded-2xl md:rounded-[28px] bg-white-card border border-line-soft overflow-hidden card-shadow"
          data-testid="comparison-table-wrap"
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-line-soft">
                  <th className="text-left px-3 md:px-6 py-4 md:py-5 text-[11px] md:text-[12px] uppercase tracking-[0.14em] text-ink-muted font-semibold w-[42%]">
                    Feature
                  </th>
                  {columns.map((c) => {
                    const isPB = c === "ProBodh";
                    return (
                      <th
                        key={c}
                        className={`text-center px-3 md:px-4 py-4 md:py-5 font-display text-[15px] md:text-[17px] ${
                          isPB ? "text-ink bg-mint/40" : "text-ink"
                        }`}
                      >
                        {c}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const [feat, ...values] = r;
                  return (
                    <tr key={i} data-testid={`comparison-row-${i}`}>
                      <td className="px-3 md:px-6 py-3.5 md:py-4 border-b border-line-soft text-[13px] md:text-[15px] text-ink leading-snug">
                        {feat}
                      </td>
                      {values.map((v, j) => (
                        <Cell
                          key={j}
                          value={v}
                          isPB={j === values.length - 1}
                        />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
